import { ClientPersonaLink } from "components/clients/list-client/client-persona-link";
import { Badge } from "components/ui/badge";
import { defaultCardStyle } from "components/ui/card";
import { ReferenceContactLink } from "components/ui/entity-link";
import { InternalLink } from "components/ui/internal-link";
import React from "react";
import { useCrmMode } from "store/selectors/hooks";
import { IncomingTransferActions } from "./incoming-transfer-actions";
import { OutgoingTransferActions } from "./outgoing-transfer-actions";
import { formatTransferDate, transferRecordFields } from "./transfer-record-fields";
import { transferStatusBadge } from "./transfer-status";
import {
    ClientTransferSubject,
    TransferDirection,
    transferDisplayName,
    TransferRow,
} from "./transfer-types";

const kindLabels: Record<ClientTransferSubject["kind"], string> = {
    placement: "Placement",
    homeCareLead: "Home Care",
};

const fieldGridClasses = "grid grid-cols-2 gap-x-4 gap-y-2";
const fieldLabelClasses = "text-xs font-medium text-gray-500";
const fieldValueClasses = "text-sm text-gray-900";

interface CardRowProps {
    row: TransferRow;
    direction: TransferDirection;
}

const CardField: React.FC<{ label: string; children: React.ReactNode }> = ({
    label,
    children,
}) => (
    <div>
        <dt className={fieldLabelClasses}>{label}</dt>
        <dd className={fieldValueClasses}>{children}</dd>
    </div>
);

const TransferNote: React.FC<{ note: string | null }> = ({ note }) => {
    const text = note?.trim() ?? "";
    if (text === "") {
        return null;
    }
    return (
        <div className="col-span-2">
            <dt className={fieldLabelClasses}>Note</dt>
            <dd className={`${fieldValueClasses} whitespace-pre-wrap break-words`}>
                {text}
            </dd>
        </div>
    );
};

const CardHeader: React.FC<{ row: TransferRow }> = ({ row }) => {
    const badge = transferStatusBadge(row.status);
    return (
        <div className="flex items-start justify-between gap-4">
            <div className="min-w-0 flex-1">
                <ClientPersonaLink
                    size="small"
                    client={{
                        appLink: row.sentClientAppLink,
                        name: row.sentClientName ?? undefined,
                        bestContactName: row.sentClientBestContactName ?? undefined,
                    }}
                />
            </div>
            <div className="text-right">
                <Badge type={badge.type} value={badge.label} />
                {row.statusChangedAt ? (
                    <div className="mt-0.5 text-xs text-gray-500">
                        {formatTransferDate(row.statusChangedAt)}
                    </div>
                ) : null}
            </div>
        </div>
    );
};

const CounterpartName: React.FC<CardRowProps> = ({ row, direction }) => {
    const name = direction === "incoming" ? row.sentByName : row.sentToName;
    if (!row.counterpartContactId) {
        return <>{name}</>;
    }
    return (
        <ReferenceContactLink referenceContactId={row.counterpartContactId} name={name} />
    );
};

const Counterpart: React.FC<CardRowProps> = ({ row, direction }) => (
    <>
        <CounterpartName row={row} direction={direction} />
        {direction === "outgoing" && row.sentToBusinessName ? (
            <div className="text-xs text-gray-500">{row.sentToBusinessName}</div>
        ) : null}
    </>
);

const TransferDetails: React.FC<CardRowProps> = ({ row, direction }) => {
    const { mode } = useCrmMode();
    return (
        <dl className={`mt-3 ${fieldGridClasses}`}>
            <CardField label={direction === "incoming" ? "From" : "To"}>
                <Counterpart row={row} direction={direction} />
            </CardField>
            <CardField label={direction === "incoming" ? "Sent to" : "Sent by"}>
                {direction === "incoming" ? row.sentToName : row.sentByName}
            </CardField>
            {mode === "combined" ? (
                <CardField label="Type">{kindLabels[row.kind]}</CardField>
            ) : null}
            <CardField label="Sent">{formatTransferDate(row.sentAt)}</CardField>
            <TransferNote note={row.note} />
        </dl>
    );
};

const receivedRecordHeading = (direction: TransferDirection): string =>
    direction === "incoming" ? "Your record" : "External record";

const ReceivedClientName: React.FC<{ row: TransferRow }> = ({ row }) => {
    const name = transferDisplayName(
        row.receivedClientName,
        row.receivedClientBestContactName,
    );
    if (!row.receivedClientAppLink) {
        return <>{name}</>;
    }
    return <InternalLink to={row.receivedClientAppLink}>{name}</InternalLink>;
};

const ReceivedRecordFields: React.FC<{ row: TransferRow }> = ({ row }) => (
    <dl className={`mt-2 ${fieldGridClasses}`}>
        <CardField label="Client">
            <ReceivedClientName row={row} />
        </CardField>
        <CardField label="Status">{row.clientStatus ?? "No status"}</CardField>
        {transferRecordFields(row).map(field => (
            <CardField key={field.label} label={field.label}>
                {field.value}
            </CardField>
        ))}
    </dl>
);

const ReceivedRecordBody: React.FC<{ row: TransferRow }> = ({ row }) => {
    if (!row.receivedRecordAvailable) {
        return <p className="mt-2 text-sm text-gray-500">No longer available</p>;
    }
    return <ReceivedRecordFields row={row} />;
};

const ReceivedRecord: React.FC<CardRowProps> = ({ row, direction }) => {
    if (row.status !== "Accepted" || !row.receivedRecordShared) {
        return null;
    }
    return (
        <div className="p-4">
            <div className="text-xs font-semibold uppercase tracking-wide text-gray-500">
                {receivedRecordHeading(direction)}
            </div>
            <ReceivedRecordBody row={row} />
        </div>
    );
};

interface TransferCardProps {
    row: TransferRow;
    direction: TransferDirection;
    onTransferChanged: () => void;
}

const PendingActions: React.FC<TransferCardProps> = props => {
    const { row, direction, onTransferChanged } = props;
    switch (direction) {
        case "incoming":
            return (
                <IncomingTransferActions
                    transfer={{
                        id: row.id,
                        kind: row.kind,
                        includeNotes: row.includeNotes,
                        includeFiles: row.includeFiles,
                    }}
                    onTransferChanged={onTransferChanged}
                />
            );
        case "outgoing":
            return (
                <OutgoingTransferActions
                    clientTransferRequestId={row.id}
                    onTransferChanged={onTransferChanged}
                />
            );
    }
    const exhaustiveCheck: never = direction;
    throw new Error(`Unhandled transfer direction ${String(exhaustiveCheck)}`);
};

const CardActions: React.FC<TransferCardProps> = props => {
    if (props.row.status !== "Pending") {
        return null;
    }
    return (
        <div className="px-4 py-3">
            <PendingActions {...props} />
        </div>
    );
};

const TransferCard: React.FC<TransferCardProps> = props => {
    const { row, direction } = props;
    return (
        <li className={`${defaultCardStyle} divide-y divide-gray-200 overflow-hidden`}>
            <div className="p-4">
                <CardHeader row={row} />
                <TransferDetails row={row} direction={direction} />
            </div>
            <ReceivedRecord row={row} direction={direction} />
            <CardActions {...props} />
        </li>
    );
};

interface TransferCardListProps {
    direction: TransferDirection;
    rows: TransferRow[];
    onTransferChanged: () => void;
}

export const TransferCardList: React.FC<TransferCardListProps> = props => {
    const { direction, rows, onTransferChanged } = props;
    return (
        <ul className="w-full max-w-2xl space-y-4">
            {rows.map(row => (
                <TransferCard
                    key={row.id}
                    row={row}
                    direction={direction}
                    onTransferChanged={onTransferChanged}
                />
            ))}
        </ul>
    );
};
